import React, { useState, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useWeb3 } from '@/hooks/useWeb3';
import { useTeamDistribution } from '@/hooks/useTeamDistribution';
import { WalletButton } from '@/components/WalletButton';
import { NetworkSwitchDialog } from '@/components/NetworkSwitchDialog';
import { AdminPanel } from '@/components/AdminPanel';
import { UserDashboard } from '@/components/UserDashboard';

export const App: React.FC = () => {
  const web3 = useWeb3();
  const teamDist = useTeamDistribution();
  const [viewMode, setViewMode] = useState<'user' | 'admin'>('user');

  // Computed values
  const formattedAccount = useMemo(() => {
    return web3.address ? web3.formatAddress(web3.address) : '';
  }, [web3.address, web3.formatAddress]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme="dark"
        toastClassName="!bg-dark-lighter !text-gray-100 !border !border-primary/20 !rounded-xl !shadow-lg"
        bodyClassName="!text-sm !font-medium"
        closeButton={false}
        hideProgressBar={false}
        newestOnTop={true}
        pauseOnFocusLoss={false}
        pauseOnHover={true}
        draggable={false}
      />
      <NetworkSwitchDialog
        isOpen={web3.showNetworkDialog}
        onSwitchNetwork={web3.switchToCorrectNetwork}
        onClose={web3.closeNetworkDialog}
      />

      <div className="min-h-screen bg-dark-light grid-pattern">
        <nav className="border-b border-primary/10 glass-effect">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-lg shadow-primary/30">
                  <svg className="w-6 h-6 text-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gradient-primary">Token Unlock</h1>
                  <p className="text-xs text-primary/60">SaharaLabs AI</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {web3.isConnected && teamDist.isOwner && (
                  <div className="flex items-center space-x-2 mr-4">
                    <button
                      onClick={() => setViewMode('user')}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                        viewMode === 'user'
                          ? 'bg-primary text-dark'
                          : 'bg-dark-lighter text-gray-400'
                      }`}
                    >
                      User View
                    </button>
                    <button
                      onClick={() => setViewMode('admin')}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                        viewMode === 'admin'
                          ? 'bg-primary text-dark'
                          : 'bg-dark-lighter text-gray-400'
                      }`}
                    >
                      Admin Panel
                    </button>
                  </div>
                )}
                <WalletButton />
              </div>
            </div>
          </div>
        </nav>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {teamDist.error && (
            <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 backdrop-blur-sm">
              <div className="flex items-start justify-between">
                <p className="text-red-200 text-sm">{teamDist.error}</p>
                <button onClick={() => teamDist.setError(null)} className="text-red-400 hover:text-red-300">✕</button>
              </div>
            </div>
          )}

          {!web3.isConnected ? (
            <div className="max-w-6xl mx-auto">
              {/* Hero Section */}
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-primary rounded-2xl mb-8 shadow-2xl shadow-primary/30 animate-float">
                  <svg className="w-12 h-12 text-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
                <h2 className="text-5xl font-bold text-gradient-primary mb-4">SaharaLabs Team Token Distribution</h2>
                <p className="text-gray-400 mb-8 max-w-2xl mx-auto text-lg">
                  Securely manage and claim your team token grants through our streamlined interface. Connect your wallet to access your personalized vesting schedule.
                </p>
                <button
                  onClick={web3.connectWallet}
                  className="px-10 py-4 bg-primary hover:bg-primary-light text-dark font-bold rounded-xl transition-all duration-200 shadow-2xl shadow-primary/30 hover:shadow-primary/50 hover:scale-105 text-lg animate-glow"
                >
                  Connect Wallet to Get Started
                </button>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 mb-12">
                <div className="glass-effect border border-primary/10 rounded-2xl p-6 text-center hover:border-primary/20 transition-all duration-300 card-glow card-glow-hover">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-xl mb-4">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-200 mb-3">Secure & Transparent</h3>
                  <p className="text-gray-400 text-sm">Built on blockchain technology with smart contracts ensuring trustless, tamper-proof token distribution.</p>
                </div>

                <div className="glass-effect border border-primary/10 rounded-2xl p-6 text-center hover:border-primary/20 transition-all duration-300 card-glow card-glow-hover">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-xl mb-4">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-200 mb-3">Flexible Vesting</h3>
                  <p className="text-gray-400 text-sm">Support for multiple grants per team member with both standard and custom vesting schedules. Employment-based vesting with TGE-based unlocks.</p>
                </div>

                <div className="glass-effect border border-primary/10 rounded-2xl p-6 text-center hover:border-primary/20 transition-all duration-300 card-glow card-glow-hover">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-xl mb-4">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-200 mb-3">Instant Claims</h3>
                  <p className="text-gray-400 text-sm">Claim your unlocked tokens anytime with one-click transactions. Track your vesting progress in real-time.</p>
                </div>
              </div>

              {/* Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-200 mb-2">How It Works</h4>
                      <ul className="space-y-2 text-sm text-gray-400">
                        <li className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <span>Connect your eligible wallet to view your allocation</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <span>Monitor your vesting progress and upcoming unlocks</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <span>Claim available tokens directly to your wallet</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-200 mb-2">Vesting Schedule</h4>
                      <ul className="space-y-2 text-sm text-gray-400">
                        <li className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <span><strong className="text-gray-300">Employment:</strong> Vesting based on employment date</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <span><strong className="text-gray-300">Unlock:</strong> Tokens unlock based on TGE date (1-year cliff + 3 years)</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <span>Multiple grants supported for bonuses and promotions</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Requirements */}
              <div className="mt-8 glass-effect border border-primary/20 rounded-2xl p-6 card-glow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-gray-200 mb-2">Requirements</h4>
                    <p className="text-sm text-gray-400">
                      Connect your wallet using MetaMask, WalletConnect, Coinbase Wallet, or any other compatible Web3 wallet.
                      Ensure you're connected to the correct network. Only wallets with allocated tokens will have access to the vesting dashboard.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : !teamDist.isEligible && !teamDist.isOwner ? (
            teamDist.isLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="custom-spinner mb-6"></div>
                <h2 className="text-xl font-semibold text-gray-300">Loading vesting data...</h2>
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-500/20 rounded-full mb-6">
                  <svg className="w-10 h-10 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-200 mb-4">No Vesting Schedule Found</h2>
                <p className="text-gray-400 mb-4">
                  Your wallet <span className="text-primary font-mono">{formattedAccount}</span> is not registered as a team member.
                </p>
                {teamDist.isOwner && (
                  <>
                    <p className="text-gray-300 mt-4">However, you are the owner of the distribution contract. Switch to Admin Panel to manage grants.</p>
                    <button
                      onClick={() => setViewMode('admin')}
                      className="mt-4 px-6 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200 shadow-lg shadow-primary/20"
                    >
                      Go to Admin Panel
                    </button>
                  </>
                )}
              </div>
            )
          ) : (
            <>
              {viewMode === 'admin' && teamDist.isOwner ? (
                <AdminPanel />
              ) : (
                <UserDashboard />
              )}
            </>
          )}
        </main>

        <footer className="mt-20 border-t border-primary/10 py-8 glass-effect">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
            <p>© 2025 SaharaLabs AI. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
};
